import { Calendar, Car, Mail, ChevronRight } from 'lucide-react';
import type { CustomerVehicle, CustomerJob, CustomerEmailLogRow } from './types';

interface OverviewTabProps {
    vehicles: CustomerVehicle[];
    jobs: CustomerJob[];
    emails: CustomerEmailLogRow[];
    diagnosticsCount: number;
    serviceHistoryCount: number;
    onSelectTab: (tab: string) => void;
}

/**
 * Landing tab — an at-a-glance read (most recent job, most recent vehicle,
 * last few emails) so a mechanic doesn't have to open every tab to get
 * their bearings on a customer they haven't seen in a while.
 */
export function OverviewTab({ vehicles, jobs, emails, diagnosticsCount, serviceHistoryCount, onSelectTab }: OverviewTabProps) {
    const latestVehicle = vehicles[0];
    const latestJob = jobs[0];
    const recentEmails = emails.slice(0, 3);
    const hasNothing = vehicles.length === 0 && jobs.length === 0 && emails.length === 0;

    if (hasNothing) {
        return (
            <section className="bg-panel border border-line rounded-xl p-8 text-center text-fg-4 text-sm">
                Nothing on record yet — add a vehicle or start a job to see this customer's activity here.
            </section>
        );
    }

    return (
        <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
                {/* Most recent vehicle */}
                <button
                    onClick={() => onSelectTab('vehicles')}
                    className="text-left bg-panel border border-line rounded-xl p-4 hover:border-accent/40 transition-colors"
                >
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-fd-sm-tight font-bold text-fg-4 uppercase tracking-wider flex items-center gap-1.5">
                            <Car className="w-3.5 h-3.5" /> Most recent vehicle
                        </span>
                        <ChevronRight className="w-4 h-4 text-fg-4" />
                    </div>
                    {latestVehicle ? (
                        <>
                            <div className="flex items-center gap-2">
                                <span className="font-mono bg-raised text-fg-2 text-xs px-1.5 py-0.5 rounded border border-tint/10">
                                    {latestVehicle.vrm}
                                </span>
                                <h4 className="font-bold text-fg">{latestVehicle.year} {latestVehicle.make} {latestVehicle.model}</h4>
                            </div>
                            {latestVehicle.mot_expiry && (
                                <p className="text-xs text-fg-4 mt-1.5">
                                    MOT due {new Date(latestVehicle.mot_expiry).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </p>
                            )}
                            {vehicles.length > 1 && (
                                <p className="text-xs text-fg-4 mt-1">+{vehicles.length - 1} more vehicle{vehicles.length - 1 !== 1 ? 's' : ''}</p>
                            )}
                        </>
                    ) : (
                        <p className="text-sm text-fg-4">No vehicles on record.</p>
                    )}
                </button>

                {/* Most recent job */}
                <button
                    onClick={() => onSelectTab('activity')}
                    className="text-left bg-panel border border-line rounded-xl p-4 hover:border-accent/40 transition-colors"
                >
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-fd-sm-tight font-bold text-fg-4 uppercase tracking-wider flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5" /> Most recent job
                        </span>
                        <ChevronRight className="w-4 h-4 text-fg-4" />
                    </div>
                    {latestJob ? (
                        <>
                            <h4 className="font-bold text-fg">
                                {latestJob.vehicles?.vrm} • {latestJob.vehicles?.make} {latestJob.vehicles?.model}
                            </h4>
                            <p className="text-sm text-fg-3 line-clamp-1 mt-0.5">{latestJob.description || 'No description'}</p>
                            <p className="text-xs text-fg-4 mt-1.5">
                                {new Date(latestJob.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </p>
                        </>
                    ) : (
                        <p className="text-sm text-fg-4">No jobs on record.</p>
                    )}
                </button>
            </div>

            {/* Quick counts linking to the other tabs */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                    { label: 'Jobs', count: jobs.length, tab: 'activity' },
                    { label: 'Diagnostics', count: diagnosticsCount, tab: 'diagnostics' },
                    { label: 'Emails', count: emails.length, tab: 'communication' },
                    { label: 'Service records', count: serviceHistoryCount, tab: 'service_history' },
                ].map((s) => (
                    <button
                        key={s.tab}
                        onClick={() => onSelectTab(s.tab)}
                        className="bg-panel border border-line rounded-xl p-3 text-center hover:border-accent/40 transition-colors"
                    >
                        <div className="text-xl font-bold text-fg font-mono">{s.count}</div>
                        <div className="text-fd-3xs uppercase font-bold text-fg-4 tracking-wider mt-0.5">{s.label}</div>
                    </button>
                ))}
            </div>

            {/* Last 3 communications */}
            {recentEmails.length > 0 && (
                <section className="bg-panel border border-line rounded-xl overflow-hidden">
                    <div className="p-4 border-b border-line flex items-center justify-between bg-raised">
                        <h3 className="font-bold text-fg flex items-center gap-2">
                            <Mail className="w-4 h-4 text-accent" />
                            Recent communication
                        </h3>
                        <button
                            onClick={() => onSelectTab('communication')}
                            className="text-xs font-bold text-accent hover:underline"
                        >
                            View all
                        </button>
                    </div>
                    <div className="divide-y divide-tint/5">
                        {recentEmails.map((mail) => (
                            <div key={mail.id} className="p-4">
                                <div className="flex items-center gap-2 text-xs text-fg-3">
                                    <Calendar className="w-3.5 h-3.5 text-fg-4" />
                                    {new Date(mail.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </div>
                                <h4 className="font-bold text-fg mt-1 truncate">{mail.subject || '(no subject)'}</h4>
                            </div>
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
}
